import { useParams, useNavigate } from 'react-router-dom';
import useApi from "../../../utility/Hooks/Api/useApi";
import React, { useEffect, useState } from "react";
import { CompanyApi } from "../../../services/Api/CompanyApi";
import { PartsApi } from "../../../services/Api/PartsApi";
import { UsersApi } from '../../../services/Api/UsersApi';
import { IdentificationIcon, MinusSmallIcon, PlusSmallIcon } from "@heroicons/react/24/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarcode } from "@fortawesome/free-solid-svg-icons";
import RemarkTextArea from "../../../components/Inputs/Remark/RemarkTextArea";
import moment from "moment";

export default function PartsDetailView() {
    const { partsId: id } = useParams();
    const [editablePartsData, setEditablePartsData] = useState({});
    const { request: partsRequest, data: partsData, loading: partsLoading } = useApi();
    const { request: partsEhuRequest, data: partsEhuData, loading: partsEhuLoading } = useApi();
    const { request: partsVehicleRequest, data: partsVehicleData, loading: partsVehicleLoading } = useApi();
    const { request: companyRequest, data: companyData, loading: companyLoading } = useApi();
    const { request: partsRemarkRequest, data: partsRemarkData, loading: partsRemarkLoading } = useApi();
    const { request: accountRequest, data: accountData, loading: accountLoading } = useApi();
    const [newRemark, setNewRemark] = useState("");
    const [editRemark, setEditRemark] = useState({ id: null, text: "" });
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                await accountRequest(UsersApi.CurrentUser())
                await partsRequest(PartsApi.getPart(id));
                await partsEhuRequest(PartsApi.getElectronicHousingUnit(id));
                await partsVehicleRequest(PartsApi.getVehicles(id));
                await companyRequest(CompanyApi.getCompanies());
            } catch (error) {

            } finally {
                setLoading(false)
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        setEditablePartsData(partsData)
    }, [partsData])

    const newRemarkHandler = (e) => {
        if (editRemark.id) {
            setEditRemark({ ...editRemark, text: e.target.value })
        } else {
            setNewRemark(e.target.value)
        }
    };

    const newRemarkRequestHandler = async (e) => {

        if (editRemark.id && editRemark.text !== "") {
            const obj = {
                "id": editRemark.id,
                "remark": editRemark.text.toString()
            };

            try {
                await partsRemarkRequest(PartsApi.editRemark(obj.id, obj.remark));
                await partsRequest(PartsApi.getPart(id));
                setEditRemark({ id: null, text: "" })
            } catch (error) {

            }

        } else {
            if (newRemark !== "") {
                const obj = {
                    "remark": newRemark.toString()
                };
                try {
                    await partsRequest(PartsApi.remarkPart(id, obj));
                } catch (error) {

                }
                setNewRemark("")
            }
        }
    };

    const newRemarkCancelHandler = (e) => {
        if (editRemark.id) {
            setEditRemark({ id: null, text: "" })
        } else {
            setNewRemark("")
        }
    };

    const reverseArray = (arr) => {
        return arr.slice().reverse()
    };

    const getCaseInsensitive = (obj, key) => {
        if (!obj) return undefined;
        const lowerCaseKey = key.toLowerCase();
        return Object.keys(obj).reduce((result, currentKey) => {
            return currentKey.toLowerCase() === lowerCaseKey ? obj[currentKey] : result;
        }, undefined);
    };

    const EditableField = ({ label, value, backgroundColor, route }) => {
        const handleClick = () => {
            if (route) navigate(`${route}`);
        };

        return (
            <div className={`flex py-2 border-t border-blue-100 px-4 text-sm ${backgroundColor}`}>
                <div className="w-1/2 font-semibold">{label}</div>
                <div className="w-1/2">
                    <span
                        className={`text-gray-700 ${route ? 'cursor-pointer hover:underline' : ''}`}
                        onClick={route ? handleClick : undefined}
                    >
                        {value ? value : 'N/A'}
                    </span>
                </div>
            </div>
        );
    };

    const handleRemarkDelete = async (remarkId) => {
        try {
            await partsRemarkRequest(PartsApi.deleteRemark(remarkId));
            await partsRequest(PartsApi.getPart(id));
        } catch (error) {

        }

    }

    const handleRemarkEdit = async (id, text) => {
        setEditRemark({ id, text });
    }


    const EditableFieldsList = ({
        type,
        editableData,
    }) => {

        const electronicHousingUnit = partsEhuData?.[0];
        const vehicle = partsVehicleData?.[0];

        const basicInformation = [
            { label: "Serial number", value: editableData["serialNumber"], field: "serialNumber" },
            { label: "Type", value: editableData["specification"]["PartType"], field: "PartType" },
            { label: "Supplier", value: editableData["supplier"], field: "supplier" },
            { label: "Order number", value: editableData["orderNumber"], field: "orderNumber" },
            { label: "Order date", value: editableData["orderDate"], field: "orderDate" },
            { label: "Shipping date", value: editableData["shippingDate"], field: "shippingDate" },
            { label: "Last seen", value: editableData["location"], field: "location" },
            ...(
                electronicHousingUnit
                    ? [{
                        label: "Electronic Housing Unit",
                        value: electronicHousingUnit["productCode"],
                        field: "productCode",
                        route: `/main/electronic-housing-unit/${electronicHousingUnit["id"]}`
                    }]
                    : []
            ),
            ...(
                vehicle
                    ? [{
                        label: "Vehicle",
                        value: getCaseInsensitive(vehicle, "reference"),
                        field: "reference",
                        route: `/main/vehicle/${vehicle["id"]}`
                    }]
                    : []
            )
        ];


        const specificationInformation = editableData["specification"]["Details"].map((spec) => ({
            label: spec?.["Key"],
            value: spec?.["Value"],
            field: spec?.["Key"]
        }));

        return (
            <div className="border border-blue-100 rounded w-full">
                {/* Column headers */}
                <div className="flex py-2 px-4 font-bold text-gray-800 bg-blue-100/75 text-sm">
                    <div className="w-1/2">Field</div>
                    <div className="w-1/2">Value</div>
                </div>

                {/* List of fields */}
                {(type === "Parts Information" ? basicInformation : specificationInformation).map((item, index) => (
                    <EditableField
                        key={item.field}
                        label={item.label}
                        value={item.value}
                        route={item.route}
                        backgroundColor={index % 2 === 0 ? 'bg-white' : 'bg-blue-100/20'}
                    />
                ))}
            </div>
        );
    };

    const InfoSection = ({
        title,
        partsData
    }) => {
        const [editablePartsData, setEditablePartsData] = useState({ ...partsData });

        return (
            <div className="w-full px-4">
                <div className="flex items-center justify-between border-b-2 border-gray-100 mt-2 mb-4">
                    <h3 className="font-semibold text-lg text-gray-800">{title}</h3>
                </div>
                <EditableFieldsList
                    type={title}
                    editableData={editablePartsData}
                />
            </div>
        );
    };

    return (
        <>
            {!loading ?
                <div className="relative container mx-auto bg-white rounded-lg shadow-2xl min-h-[46.4rem]">
                    <div className="mb-1 sm:mb-4 justify-between w-full items-center">
                        <div>
                            <div className="mb-0 px-4 pt-4 border-0">
                                <div className="flex lg:flex-row flex-col">
                                    <div className="relative px-4 max-w-full">
                                        <h3 className="font-semibold text-lg text-gray-800">{partsData["specification"]["PartType"]}</h3>
                                    </div>
                                    <div className="relative px-4 max-w-full flex lg:flex-col text-sm lg:border-l-2">
                                        <span className="flex lg:mr-0 mr-1">Status:</span>
                                        <span className="flex text-indigo-600">{partsData["reference"]}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="rounded-t mb-0 pt-1 pb-4 px-4 border-0">
                                    <div className="relative px-4 max-w-full flex flex-row items-center">
                                        <IdentificationIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                                        <span className="text-gray-500 px-2 text-sm">{id}</span>
                                    </div>
                                    <div className="relative px-4 max-w-full flex flex-row items-center">
                                        <FontAwesomeIcon
                                            icon={faBarcode}
                                            className="h-4 w-5 text-gray-500"
                                        />
                                        <span className="text-gray-500 px-2 text-sm">{partsData["serialNumber"]}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="relative lg:flex mb-1 sm:mb-4">
                                <div className="flex flex-row mb-1 justify-between w-full lg:mr-2">
                                    <div className="mb-0 px-4 py-4 w-full">
                                        <InfoSection
                                            title="Parts Information"
                                            partsData={editablePartsData}
                                        ></InfoSection>
                                    </div>
                                </div>
                                <div className="flex flex-row mb-1 justify-between w-full lg:ml-2">
                                    <div className="mb-0 px-4 py-4 border-0 w-full">
                                        <InfoSection
                                            title="Specifications"
                                            partsData={editablePartsData}
                                        ></InfoSection>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row mb-1 justify-between w-full items-center lg:mr-2 pb-4">
                                <div className="mb-0 px-4 py-4 border-0 w-full">
                                    <div className="flex flex-wrap items-center">
                                        <div className="relative w-full px-4 max-w-full flex-grow flex-1"><h3
                                            className="font-semibold text-lg text-gray-800 border-b-2 border-gray-100 py-2">Remarks</h3>
                                        </div>
                                    </div>
                                    <RemarkTextArea
                                        required
                                        onChange={e => newRemarkHandler(e)}
                                        value={newRemark}
                                        confirm={() => newRemarkRequestHandler()}
                                        cancel={() => newRemarkCancelHandler()} />
                                    {reverseArray(partsData["remarks"]).map((remark, index) => {
                                        return (
                                            <div key={remark["Id"]} className="relative w-full px-4 py-4 max-w-full border-b border-gray-200">
                                                <div className="flex justify-between items-start">
                                                    <div className="w-full">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-indigo-600 font-medium">{remark["Name"]}</span>
                                                                <span className="text-gray-400 text-sm">{moment(remark["On"]).format("DD-MM-yyyy")}</span>
                                                            </div>
                                                            {/* Edit/Delete Buttons — only if not deleted */}
                                                            {remark.RemovedOn === "0001-01-01T00:00:00+00:00" && (
                                                                (remark["By"] === accountData["id"] || accountData.roles?.includes("administrator")) && (
                                                                    <div className="flex space-x-2">
                                                                        {remark["By"] === accountData["id"] && (
                                                                            <button
                                                                                onClick={() => handleRemarkEdit(remark["Id"], remark["Text"])}
                                                                                className="text-blue-500 hover:text-blue-700 text-sm"
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                        )}
                                                                        {(accountData.roles?.includes("administrator") || remark["By"] === accountData["id"]) && (
                                                                            <button
                                                                                onClick={() => handleRemarkDelete(remark["Id"])}
                                                                                className="text-red-500 hover:text-red-700 text-sm"
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                        {/* Remark Text or Deleted Notice */}
                                                        <div className="text-sm mt-2 text-gray-600">
                                                            {remark.RemovedOn !== "0001-01-01T00:00:00+00:00" && remark.DeletedName ? (
                                                                <div className="italic text-red-500">
                                                                    This remark was deleted by {remark.DeletedName} on{" "}
                                                                    {moment(remark.RemovedOn).format("LLL")}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    {editRemark.id === remark["Id"] ? <RemarkTextArea
                                                                        required
                                                                        onChange={e => newRemarkHandler(e)}
                                                                        value={editRemark?.text}
                                                                        confirm={() => newRemarkRequestHandler()}
                                                                        cancel={() => newRemarkCancelHandler()} /> : <pre className="whitespace-pre-wrap break-words text-sm text-gray-600 font-sans">{remark["Text"]}</pre>}
                                                                    {/* Edited Info */}
                                                                    {remark.ModifiedOn !== "0001-01-01T00:00:00+00:00" && remark.EditedName && (
                                                                        <div className="text-xs text-gray-400 mt-1 italic">
                                                                            Edited by {remark.EditedName} on {moment(remark.ModifiedOn).format("LLL")}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                : null}
        </>
    )
}